import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import {
  CheckOutlined,
  CloseCircleOutlined,
  CreditCardOutlined,
  DisconnectOutlined,
  LinkOutlined,
  PercentageOutlined,
  QuestionCircleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Row,
  Col,
  Typography,
  Divider,
  Button,
  InputNumber,
  Tooltip,
  Select,
  Space,
  Flex,
  Switch,
  Tag,
  Spin,
} from 'antd';
import styled from 'styled-components';
import { COMPANY_NAME } from '../../../constants';
import { t } from '../../../utils/i18n';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import useMerchantSettings from '../../../hooks/use-merchant-settings';
import {
  useDisablePayPalIntegration,
  useInitiatePayPalSetup,
  useInitiateShorePayOnlineAccountSetup,
  useFetchSubPaymentMethodsV2,
  useToggleSubPaymentMethodV2,
  useUpdatePaymentMethodsV2,
  paymentErrors,
} from '../../../shared/hooks/payments';
import { PaymentCaptureTaxIDModal } from './PaymentCaptureTaxIDModal';
import {
  PaymentAvailabilityType,
  PaymentMethodStatus,
  IPaymentMethodV2,
  IPaymentMethodMetaV2,
  IMerchantPaymentSettingsV2,
  ISubPaymentMethodV2,
  SubPaymentMethodNameV2,
} from '../../../shared/types';
import createQueryConfig from '../../../queries';
import {
  VisaIcon,
  MastercardIcon,
  MaestroIcon,
  AmexIcon,
  CbIcon,
  ApplePayIcon,
  GooglePayIcon,
  KlarnaIcon,
} from './PaymentBrandIcons';
import { getHelpCenterLink } from '@shared/utils';

const DOWNPAYMENT_MIN = 10;

interface SectionPaymentPolicyProps {
  currentPaymentAvailability: PaymentAvailabilityType;
  onPaymentPolicyChange: (value: PaymentAvailabilityType) => void;
  isLoading: boolean;
  isTrialMerchant: boolean;
  hasNoActivePaymentMethod: boolean;
  hideOptionalPolicy: boolean;
  paymentPageHelpCenterLink: string;
}

interface SectionPrepaymentAmountProps {
  amount: number;
  onAmountChange: (value: number) => void;
  disabled: boolean;
}

interface ShorePayOnlineCardProps {
  merchantId: string;
  status: PaymentMethodStatus;
  company: string;
  isTrialMerchant: boolean;
}

interface PayPalCardProps {
  merchantId: string;
  status: PaymentMethodStatus;
  isTrialMerchant: boolean;
  onDisconnectSuccess: () => void;
}

interface SectionPaymentGatewayProps {
  merchantId: string;
  company: string;
  isTrialMerchant: boolean;
  isPackageCodeBooking: boolean;
  isPayPalFeatureToggleEnabled: boolean;
  paymentMethods: IPaymentMethodV2[];
  onPayPalDisconnectSuccess: () => void;
}

const StyledPaymentSetupPageWrapper = styled.div`
  width: 100%;
  max-width: 650px;
  padding: 0 24px 24px;
`;

const StyledGatewayCard = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
`;

const StyledGatewayCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
`;

const StyledSubPaymentMethodRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
`;

// ── Section: Payment Policy ──────────────────────────────────────────

function SectionPaymentPolicy({
  currentPaymentAvailability,
  onPaymentPolicyChange,
  isLoading,
  isTrialMerchant,
  hasNoActivePaymentMethod,
  hideOptionalPolicy,
  paymentPageHelpCenterLink,
}: SectionPaymentPolicyProps) {
  const PAYMENT_POLICY_OPTIONS = [
    {
      value: 'disabled' as PaymentAvailabilityType,
      label: t(
        'application-settings.payments.setup-page.section-booking-payment.payment-types.pay-at-location'
      ),
      icon: <CloseCircleOutlined />,
      description: t(
        'application-settings.payments.setup-page.section-payment-policy.helper.disabled'
      ),
    },
    {
      value: 'mandatory' as PaymentAvailabilityType,
      label: t(
        'application-settings.payments.setup-page.section-booking-payment.payment-types.mandatory'
      ),
      icon: <CreditCardOutlined />,
      description: t(
        'application-settings.payments.setup-page.section-payment-policy.helper.mandatory'
      ),
    },
    {
      value: 'optional' as PaymentAvailabilityType,
      label: t(
        'application-settings.payments.setup-page.section-booking-payment.payment-types.optional'
      ),
      icon: <UserOutlined />,
      description: t(
        'application-settings.payments.setup-page.section-payment-policy.helper.optional'
      ),
    },
  ];
  const options = hideOptionalPolicy
    ? PAYMENT_POLICY_OPTIONS.filter((o) => o.value !== 'optional')
    : PAYMENT_POLICY_OPTIONS;

  const selectedOption = PAYMENT_POLICY_OPTIONS.find(
    (o) => o.value === currentPaymentAvailability
  )!;

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Flex align="center" gap={8}>
          <Typography.Title
            level={3}
            style={{ marginTop: 16, marginBottom: 4 }}
          >
            {t(
              'application-settings.payments.setup-page.section-payment-policy.title'
            )}
          </Typography.Title>
          <Typography.Link
            href={paymentPageHelpCenterLink || '#'}
            target="_blank"
            style={{ fontSize: 12 }}
          >
            {t(
              'application-settings.payments.setup-page.section-payment-policy.help-center-link'
            )}
          </Typography.Link>
        </Flex>
        <Typography.Text
          type="secondary"
          style={{ display: 'block', maxWidth: 500 }}
        >
          {t(
            'application-settings.payments.setup-page.section-payment-policy.description'
          )}
        </Typography.Text>
      </Col>
      <Col span={24} style={{ maxWidth: 500 }}>
        <Select
          value={currentPaymentAvailability}
          onChange={onPaymentPolicyChange}
          loading={isLoading}
          disabled={isLoading || isTrialMerchant || hasNoActivePaymentMethod}
          style={{ width: '100%' }}
          size="large"
          labelRender={() => (
            <Space>
              {selectedOption.icon}
              {selectedOption.label}
            </Space>
          )}
          optionRender={(option) => {
            const { icon, label, description } = option.data;
            const isSelected = option.value === currentPaymentAvailability;
            return (
              <Flex gap={8} align="center" style={{ fontWeight: 'normal' }}>
                <div>{icon}</div>
                <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                  <Typography.Text style={{ whiteSpace: 'normal' }}>
                    {label}
                  </Typography.Text>
                  <Typography.Text
                    type="secondary"
                    style={{ whiteSpace: 'normal' }}
                  >
                    {description}
                  </Typography.Text>
                </Flex>
                {isSelected && <CheckOutlined />}
              </Flex>
            );
          }}
          options={options}
        />
        <Typography.Text
          type="secondary"
          style={{ display: 'block', marginTop: 8 }}
        >
          {selectedOption.description}
        </Typography.Text>
        {currentPaymentAvailability === 'optional' && (
          <Alert
            showIcon
            message={t(
              'application-settings.payments.setup-page.section-booking-payment.warning.payment-type-optional'
            )}
            type="warning"
            style={{ marginTop: 8 }}
          />
        )}
      </Col>
    </Row>
  );
}

// ── Section: Prepayment Amount ───────────────────────────────────────

function SectionPrepaymentAmount({
  amount,
  onAmountChange,
  disabled,
}: SectionPrepaymentAmountProps) {
  return (
    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
      <Col span={24} style={{ maxWidth: 500 }}>
        <Flex align="center" gap={4} style={{ marginBottom: 4 }}>
          <Typography.Text>
            {t(
              'application-settings.payments.setup-page.section-payment-policy.prepayment-amount.label'
            )}
          </Typography.Text>
          <Tooltip
            title={t(
              'application-settings.payments.setup-page.section-payment-policy.prepayment-amount.tooltip'
            )}
          >
            <QuestionCircleOutlined style={{ color: '#bdc3c7' }} />
          </Tooltip>
        </Flex>
        <InputNumber
          size="large"
          min={10}
          max={100}
          value={amount}
          onChange={(val) => val !== null && onAmountChange(val)}
          prefix={<PercentageOutlined />}
          style={{ width: '100%' }}
          disabled={disabled}
        />
        <Typography.Text
          type="secondary"
          style={{ fontSize: 12, marginTop: 4, display: 'block' }}
        >
          {amount === 100
            ? t(
                'application-settings.payments.setup-page.section-payment-policy.prepayment-amount.full-amount-hint'
              )
            : t(
                'application-settings.payments.setup-page.section-payment-policy.prepayment-amount.remaining-hint'
              ).replace('{{remaining}}', String(100 - amount))}
        </Typography.Text>
      </Col>
    </Row>
  );
}

// ── Shore Pay Online Card ────────────────────────────────────────────

function ShorePayOnlineCard({
  merchantId,
  status,
  company,
  isTrialMerchant,
}: ShorePayOnlineCardProps) {
  const [isCaptureTaxIDModalOpen, setIsCaptureTaxIDModalOpen] = useState(false);

  const {
    mutate: mutateInitiateShorePayOnlineAccountSetup,
    isPending: isLoadingShorePayOnlineSetup,
  } = useInitiateShorePayOnlineAccountSetup();

  const {
    data: subPaymentMethods,
    isLoading: isLoadingSubPaymentMethods,
    isFetching: isFetchingSubPaymentMethods,
  } = useFetchSubPaymentMethodsV2(merchantId, status !== 'not_setup');

  const { mutate: mutateToggleSubPaymentMethod, isPending: isTogglingKlarna } =
    useToggleSubPaymentMethodV2();

  const handleToggle = (
    name: SubPaymentMethodNameV2,
    currentEnabled: boolean
  ) => {
    mutateToggleSubPaymentMethod({
      merchantId,
      subPaymentMethodName: name,
      enabled: !currentEnabled,
    });
  };

  const handleSetup = () => {
    mutateInitiateShorePayOnlineAccountSetup(
      { company, merchantId },
      {
        onError: (error: unknown) => {
          if (
            error instanceof Error &&
            error.message.includes(paymentErrors.vatIdOrTaxNumberRequired)
          ) {
            setIsCaptureTaxIDModalOpen(true);
          }
        },
      }
    );
  };

  const handleCaptureTaxIDSubmit = ({ taxId }: { taxId: string }) => {
    mutateInitiateShorePayOnlineAccountSetup(
      { taxId, company, merchantId },
      { onSuccess: () => setIsCaptureTaxIDModalOpen(false) }
    );
  };

  if (status === 'not_setup') {
    return (
      <StyledGatewayCard>
        <StyledGatewayCardHeader>
          <Typography.Text strong style={{ fontSize: 14 }}>
            Shore Pay Online
          </Typography.Text>
          <Button
            icon={<LinkOutlined />}
            type="primary"
            loading={isLoadingShorePayOnlineSetup}
            onClick={handleSetup}
            disabled={isTrialMerchant}
          >
            {t(
              'application-settings.payments.setup-page.section-payment-gateways.status.setup'
            )}
          </Button>
        </StyledGatewayCardHeader>
        {isCaptureTaxIDModalOpen && (
          <PaymentCaptureTaxIDModal
            isLoadingShorePayOnlineSetup={isLoadingShorePayOnlineSetup}
            onClickClose={() => setIsCaptureTaxIDModalOpen(false)}
            onFormSubmit={handleCaptureTaxIDSubmit}
          />
        )}
      </StyledGatewayCard>
    );
  }

  type SubPaymentMethodsMap = Record<
    SubPaymentMethodNameV2,
    ISubPaymentMethodV2
  >;
  const methods = Object.fromEntries(
    subPaymentMethods.map((m) => [m.name, m])
  ) as SubPaymentMethodsMap;

  return (
    <StyledGatewayCard>
      <StyledGatewayCardHeader>
        <Flex align="center" gap={8}>
          <Typography.Text strong style={{ fontSize: 14 }}>
            Shore Pay Online
          </Typography.Text>
          <Tag color="green" bordered>
            {t(
              'application-settings.payments.setup-page.section-payment-gateways.status.active'
            )}
          </Tag>
        </Flex>
      </StyledGatewayCardHeader>
      <Divider style={{ margin: 0 }} />
      {isLoadingSubPaymentMethods ? (
        <Flex justify="center" style={{ padding: 24 }}>
          <Spin />
        </Flex>
      ) : (
        <>
          <StyledSubPaymentMethodRow>
            <Flex align="center" gap={8}>
              <Typography.Text>
                {t(
                  'application-settings.payments.setup-page.section-payment-gateways.credit-card-payment'
                )}
              </Typography.Text>
              <Flex gap={4}>
                <VisaIcon />
                <MastercardIcon />
                <MaestroIcon />
                <AmexIcon />
                <CbIcon />
              </Flex>
            </Flex>
            <Switch
              checked={methods?.Card?.enabled}
              disabled
              style={{ opacity: 0.4 }}
            />
          </StyledSubPaymentMethodRow>
          <StyledSubPaymentMethodRow>
            <Flex align="center" gap={8}>
              <Typography.Text>Apple Pay</Typography.Text>
              <ApplePayIcon />
            </Flex>
            <Switch
              checked={methods?.ApplePay?.enabled}
              disabled
              style={{ opacity: 0.4 }}
            />
          </StyledSubPaymentMethodRow>
          <StyledSubPaymentMethodRow>
            <Flex align="center" gap={8}>
              <Typography.Text>Google Pay</Typography.Text>
              <GooglePayIcon />
            </Flex>
            <Switch
              checked={methods?.GooglePay?.enabled}
              disabled
              style={{ opacity: 0.4 }}
            />
          </StyledSubPaymentMethodRow>
          <Divider style={{ margin: 0 }} />
          <StyledSubPaymentMethodRow>
            <Flex align="center" gap={8}>
              <Typography.Text>Klarna</Typography.Text>
              <KlarnaIcon />
            </Flex>
            <Switch
              checked={methods?.Klarna?.enabled}
              loading={isTogglingKlarna || isFetchingSubPaymentMethods}
              disabled={isTogglingKlarna || isFetchingSubPaymentMethods}
              onChange={() => handleToggle('Klarna', methods?.Klarna?.enabled)}
            />
          </StyledSubPaymentMethodRow>
        </>
      )}
    </StyledGatewayCard>
  );
}

// ── PayPal Card ──────────────────────────────────────────────────────

function PayPalCard({
  merchantId,
  status,
  isTrialMerchant,
  onDisconnectSuccess,
}: PayPalCardProps) {
  const { trackMethod } = useGlobals()!;
  const isActive = status === 'active';
  const isPending = status === 'pending';

  const {
    mutate: mutateDisablePaypalIntegration,
    isPending: isLoadingDisablePaypalIntegration,
  } = useDisablePayPalIntegration();

  const {
    mutate: mutateInitiatePayPalSetup,
    isPending: isLoadingInitiatePayPalSetup,
  } = useInitiatePayPalSetup();

  const handleDisconnect = () => {
    trackMethod(
      'application-settings-app_online_payment_settings_screen_disable_paypal_modal_opened'
    );
    mutateDisablePaypalIntegration(
      { merchantId },
      { onSuccess: onDisconnectSuccess }
    );
  };

  const handleConnect = () => {
    mutateInitiatePayPalSetup({ merchantId });
  };

  return (
    <StyledGatewayCard>
      <StyledGatewayCardHeader>
        <Flex align="center" gap={8}>
          <Typography.Text strong style={{ fontSize: 14 }}>
            PayPal
          </Typography.Text>
          {isActive && (
            <Tag color="green" bordered>
              {t(
                'application-settings.payments.setup-page.section-payment-gateways.status.active'
              )}
            </Tag>
          )}
          {isPending && (
            <Tag color="orange" bordered>
              {t(
                'application-settings.payments.setup-page.section-payment-gateways.status.pending'
              )}
            </Tag>
          )}
        </Flex>
        {isActive ? (
          <Button
            icon={<DisconnectOutlined />}
            loading={isLoadingDisablePaypalIntegration}
            onClick={handleDisconnect}
          >
            {t(
              'application-settings.payments.setup-page.section-payment-gateways.status.disconnect'
            )}
          </Button>
        ) : !isPending ? (
          <Button
            icon={<LinkOutlined />}
            loading={isLoadingInitiatePayPalSetup}
            onClick={handleConnect}
            type="primary"
            disabled={isTrialMerchant}
          >
            {t(
              'application-settings.payments.setup-page.section-payment-gateways.status.connect'
            )}
          </Button>
        ) : null}
      </StyledGatewayCardHeader>
      <Divider style={{ margin: 0 }} />
      <div style={{ padding: 16 }}>
        <Typography.Text type="secondary">
          {t(
            'application-settings.payments.setup-page.section-payment-gateways.paypal-description'
          )}
        </Typography.Text>
        {isPending && (
          <Alert
            showIcon
            type="warning"
            message={t(
              'application-settings.payments.setup-page.section-payment-gateways.instructions.for-pending'
            )}
            style={{ marginTop: 8 }}
          />
        )}
      </div>
    </StyledGatewayCard>
  );
}

// ── Section: Payment Gateways ────────────────────────────────────────

function SectionPaymentGateway({
  merchantId,
  company,
  paymentMethods,
  isTrialMerchant,
  isPackageCodeBooking,
  isPayPalFeatureToggleEnabled,
  onPayPalDisconnectSuccess,
  customerSupportLink,
}: SectionPaymentGatewayProps & { customerSupportLink: string }) {
  const tooltipText = isPayPalFeatureToggleEnabled
    ? t(
        'application-settings.payments.setup-page.section-payment-gateways.title-tooltip'
      )
    : t(
        'application-settings.payments.setup-page.section-payment-gateways.title-tooltip-for-stripe-only'
      );

  const shorePayOnline = paymentMethods.find(
    (m) => m.name === 'shorepay_online'
  );
  const paypal = paymentMethods.find((m) => m.name === 'paypal');

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Typography.Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>
          {t(
            'application-settings.payments.setup-page.section-payment-gateways.title'
          )}
          <Tooltip title={tooltipText}>
            <QuestionCircleOutlined
              style={{
                fontSize: 18,
                marginLeft: 8,
                color: '#bdc3c7',
              }}
            />
          </Tooltip>
        </Typography.Title>
        <Typography.Text
          type="secondary"
          style={{ display: 'block', maxWidth: 500 }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: t(
                'application-settings.payments.setup-page.section-payment-gateways.description-v2'
              ).replace('{{customerSupportLink}}', customerSupportLink),
            }}
          />
        </Typography.Text>
      </Col>
      <Col span={24}>
        <Flex vertical gap={16} style={{ maxWidth: 500 }}>
          {shorePayOnline && (
            <ShorePayOnlineCard
              merchantId={merchantId}
              status={shorePayOnline.status}
              company={company}
              isTrialMerchant={isTrialMerchant}
            />
          )}
          {paypal && isPayPalFeatureToggleEnabled && !isPackageCodeBooking && (
            <PayPalCard
              merchantId={merchantId}
              status={paypal.status}
              isTrialMerchant={isTrialMerchant}
              onDisconnectSuccess={onPayPalDisconnectSuccess}
            />
          )}
        </Flex>
      </Col>
    </Row>
  );
}

// ── Main Component ───────────────────────────────────────────────────

export function PaymentSetupPageV2() {
  const { queryClient, apiClient } = useGlobals()!;
  const queryConfig = createQueryConfig(queryClient, apiClient);

  const { merchantSettings } = useMerchantSettings();
  const {
    merchantId,
    company,
    trialDaysLeft,
    featureToggles,
    locale,
    subscriptionPackageCode,
  } = merchantSettings || {};
  const isTrialMerchant = (trialDaysLeft || 0) > 0;

  const whiteLabelSchemaless =
    merchantSettings?.whiteLabelConfiguration?.schemaless;

  const isPackageCodeBooking = subscriptionPackageCode === 'shore_bm_booking';

  const customerSupportLink = getHelpCenterLink({
    key: 'createCustomerSupportTicket',
    config: whiteLabelSchemaless,
    locale,
    companyName: company ?? 'shore',
  });

  const paymentPageHelpCenterLink = getHelpCenterLink({
    key: 'paymentSetup',
    config: whiteLabelSchemaless,
    locale,
    companyName: company ?? 'shore',
  });

  const {
    data: paymentSettingsV2,
    isLoading,
    isFetching: isFetchingPaymentSettings,
  } = useQuery<IMerchantPaymentSettingsV2>({
    ...queryConfig.merchantPaymentSettingsV2b({ merchantId }),
    enabled: Boolean(merchantId),
  });

  const {
    mutate: mutateUpdatePaymentMethods,
    isPending: isUpdatingPaymentPolicy,
  } = useUpdatePaymentMethodsV2();

  const paymentMethods: IPaymentMethodV2[] = (
    paymentSettingsV2?.payment_methods ?? []
  ).filter(({ name, status }) => {
    if (!(featureToggles?.paypal_payment_enabled ?? false) && name === 'paypal')
      return false;
    if (COMPANY_NAME !== 'shore' && name === 'shorepay_online') return false;
    if (name === 'stripe' && status === 'not_setup') return false;
    return true;
  });

  const isShorePayOnlineActive =
    paymentMethods.find((m) => m.name === 'shorepay_online')?.status ===
    'active';
  const hasNoActivePaymentMethod = !paymentMethods.some(
    (m) => m.status === 'active'
  );
  const hideOptionalPolicy =
    paymentSettingsV2?.hide_optional_customer_payment ?? false;

  const shorePayOnlineMeta: IPaymentMethodMetaV2 | undefined =
    paymentMethods.find((m) => m.name === 'shorepay_online')?.meta;

  const [prepaymentAmount, setPrepaymentAmount] = useState<number>(
    shorePayOnlineMeta?.down_payment.percentage ?? 100
  );

  useEffect(() => {
    if (shorePayOnlineMeta?.down_payment.percentage !== undefined) {
      setPrepaymentAmount(shorePayOnlineMeta.down_payment.percentage);
    }
  }, [shorePayOnlineMeta?.down_payment.percentage]);

  const debouncedUpdateDownPayment = useMemo(
    () =>
      debounce((percentage: number) => {
        if (!merchantId) return;
        mutateUpdatePaymentMethods({
          merchantId,
          payload: {
            payment_methods: [
              {
                name: 'shorepay_online',
                meta: { down_payment: { percentage } },
              },
            ],
          },
        });
      }, 500),
    [merchantId, mutateUpdatePaymentMethods]
  );

  const isPayPalFeatureToggleEnabled =
    featureToggles?.paypal_payment_enabled || false;

  const paymentPolicy = paymentSettingsV2?.payment_policy ?? 'disabled';

  const handleOnPaymentPolicyChange = (value: PaymentAvailabilityType) => {
    if (!merchantId) return;
    switch (value) {
      case 'mandatory':
        mutateUpdatePaymentMethods({
          merchantId,
          payload: {
            payment_policy: 'mandatory',
            payment_methods: isShorePayOnlineActive
              ? [
                  {
                    name: 'shorepay_online',
                    meta: {
                      down_payment: {
                        percentage: prepaymentAmount || DOWNPAYMENT_MIN,
                      },
                    },
                  },
                ]
              : [],
          },
        });
        break;
      case 'disabled':
        mutateUpdatePaymentMethods({
          merchantId,
          payload: { payment_policy: 'disabled' },
        });
        break;
      case 'optional':
        mutateUpdatePaymentMethods({
          merchantId,
          payload: { payment_policy: 'optional' },
        });
        break;
      default:
        console.warn('Unknown payment policy:', value);
    }
  };

  const handlePrepaymentAmountChange = (value: number) => {
    setPrepaymentAmount(value);
    debouncedUpdateDownPayment(value);
  };

  const handlePayPalDisconnectSuccess = () => {
    if (!merchantId) return;
    const hasOtherActiveMethod = paymentMethods.some(
      (m) => m.name !== 'paypal' && m.status === 'active'
    );
    if (!hasOtherActiveMethod) {
      mutateUpdatePaymentMethods(
        {
          merchantId,
          payload: { payment_policy: 'disabled' },
        },
        { onSuccess: () => window.location.reload() }
      );
    } else {
      window.location.reload();
    }
  };

  if (isLoading || !merchantId) {
    return (
      <Flex justify="center" style={{ padding: 48 }}>
        <Spin />
      </Flex>
    );
  }

  return (
    <StyledPaymentSetupPageWrapper>
      <SectionPaymentPolicy
        isTrialMerchant={isTrialMerchant}
        hasNoActivePaymentMethod={hasNoActivePaymentMethod}
        currentPaymentAvailability={paymentPolicy}
        onPaymentPolicyChange={handleOnPaymentPolicyChange}
        isLoading={isUpdatingPaymentPolicy || isFetchingPaymentSettings}
        hideOptionalPolicy={hideOptionalPolicy}
        paymentPageHelpCenterLink={paymentPageHelpCenterLink || '#'}
      />
      {paymentPolicy !== 'disabled' && (
        <SectionPrepaymentAmount
          amount={
            paymentPolicy === 'optional' || !isShorePayOnlineActive
              ? 100
              : prepaymentAmount
          }
          onAmountChange={handlePrepaymentAmountChange}
          disabled={
            !isShorePayOnlineActive ||
            paymentPolicy === 'optional' ||
            isUpdatingPaymentPolicy ||
            isFetchingPaymentSettings
          }
        />
      )}
      <Divider />
      <SectionPaymentGateway
        isTrialMerchant={isTrialMerchant}
        merchantId={merchantId}
        company={company ?? COMPANY_NAME}
        paymentMethods={paymentMethods}
        isPackageCodeBooking={isPackageCodeBooking}
        isPayPalFeatureToggleEnabled={isPayPalFeatureToggleEnabled}
        onPayPalDisconnectSuccess={handlePayPalDisconnectSuccess}
        customerSupportLink={customerSupportLink || '#'}
      />
      <Typography.Text
        type="secondary"
        style={{ display: 'block', marginTop: 16 }}
      >
        <span
          dangerouslySetInnerHTML={{
            __html: t(
              'application-settings.payments.setup-page.section-transactions.transaction-overview'
            ),
          }}
        />
      </Typography.Text>
    </StyledPaymentSetupPageWrapper>
  );
}
